import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  PlayCircle,
  Square,
  FileDown,
  FileText,
  Save,
  ArrowRight,
  Lock,
  Pencil,
  RotateCcw,
  Check,
  Loader2,
} from "lucide-react";
import {
  reportTemplatesApi,
  reportSubmissionsApi,
  reportGenerationApi,
  isSystemQuestion,
  isSystemSection,
  PREPARED_FOR_QUESTION_ID,
  AUDIENCE_QUESTION_ID,
  type ReportTemplateDto,
  type ReportSubmissionDto,
  type ReportFormQuestionDto,
  type CreateReportSubmissionPayload,
} from "@/api/reportTemplates";
import { documentsApi } from "@/api/documents";
import { foldersApi } from "@/api/folders";
import { Badge, Button, FormField, Input } from "@/design-system/components/ui";
import { Select, Textarea } from "@/components/ui/Select";
import { PageHeader } from "@/components/layout/PageHeader";
import { useToast } from "@/components/ui/Toast";
import { LongFormTextarea } from "@/components/reports/LongFormTextarea";
import { AnswerAssistantButtons } from "@/components/reports/v32/AnswerAssistantButtons";
import {
  PickerDrawer,
  isPickerType,
  type PickerKind,
  type PickerRecord,
} from "@/components/reports/PickerDrawer";
import { SubmissionDocumentsSection } from "@/components/reports/v32/SubmissionDocumentsSection";
import { PreviewPromptModal } from "@/components/reports/v32/PreviewPromptModal";
import { TokenBudgetDisplay } from "@/components/reports/v32/TokenBudgetDisplay";
import { useSubmissionDocuments } from "@/hooks/reports/useSubmissionDocuments";
import { allAttachmentsReady } from "@/api/reportsV32";
import {
  WizardProgress,
  type WizardStepKey,
} from "@/components/reports/v32/WizardProgress";
import { usePreviewPrompt } from "@/hooks/reports/usePreviewPrompt";
import { useReportItemPrefill } from "@/hooks/reports/useReportItemPrefill";
import { useAuth } from "@/hooks/useAuth";

/**
 * LLM &amp; Reports v3.1 §10a — the single entry point for AI report
 * generation. Replaces v2.0's split between /reports/generate (LLM mode)
 * and /forms/submit/{id} (form-based submission). One vertical page with
 * five sections: Title → Form → Master Document → Supporting Documents →
 * Prompt Review → Run.
 */
export function ReportGeneratePage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const submissionIdParam = searchParams.get("submissionId");
  // Deal work-tab "Generate report" deep link
  // (?templateId=&dealId=&itemCode=) — the template is selected from the
  // param immediately; the pre-drafted intake (title / subject / audience /
  // goal) is fetched from the item-prefill endpoint and applied once below.
  const templateIdParam = searchParams.get("templateId");
  const dealIdParam = searchParams.get("dealId");
  const itemCodeParam = searchParams.get("itemCode");
  // Broker certification "Generate report" deep link (?templateId=&brokerId=)
  // — associates the submission with the broker so the certification
  // file-report endpoint (which requires submission.BrokerId to match) can
  // accept it later.
  const brokerIdParam = searchParams.get("brokerId");
  // The submission payload carries dealId only when it's a well-formed GUID
  // (a tampered deep link must degrade to a normal blank run, not a 400).
  const dealIdForPayload = useMemo(
    () =>
      dealIdParam &&
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        dealIdParam,
      )
        ? dealIdParam
        : null,
    [dealIdParam],
  );
  const brokerIdForPayload = useMemo(
    () =>
      brokerIdParam &&
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        brokerIdParam,
      )
        ? brokerIdParam
        : null,
    [brokerIdParam],
  );

  const templates = useQuery({
    queryKey: ["report-templates", "mine"],
    queryFn: () => reportTemplatesApi.mine(),
  });

  const [reportTemplateId, setReportTemplateId] = useState<string>(
    // Draft resume (?submissionId=) hydrates its own template selection.
    submissionIdParam ? "" : (templateIdParam ?? ""),
  );
  const [title, setTitle] = useState("");
  const [responses, setResponses] = useState<Record<string, string>>({});
  const [pickerLabels, setPickerLabels] = useState<Record<string, string>>({});
  const [submissionId, setSubmissionId] = useState<string | null>(submissionIdParam);
  const [submitted, setSubmitted] = useState(false);
  const [activePicker, setActivePicker] = useState<{
    questionId: string;
    kind: PickerKind;
  } | null>(null);

  // Prompt review state.
  const [resolvedPrompt, setResolvedPrompt] = useState<string>("");
  const [editedPrompt, setEditedPrompt] = useState<string>("");
  const [editingPrompt, setEditingPrompt] = useState(false);
  // v3.3 §3 — committed one-time edit. Save edits copies editedPrompt
  // here; startGenerate sends this value (not the live textarea) so
  // there's no question what was sent. Null = no committed edit; the
  // backend falls back to the user's saved customization or the
  // template body. promptSavedAt is set on Save edits and cleared 3s
  // later to drive the confirmation pill.
  const [committedPromptEdit, setCommittedPromptEdit] = useState<string | null>(null);
  const [promptSavedAt, setPromptSavedAt] = useState<number | null>(null);

  // Streaming state.
  const [streaming, setStreaming] = useState(false);
  const [streamedText, setStreamedText] = useState<string>("");
  const [streamedReportId, setStreamedReportId] = useState<string | null>(null);
  const [abortCtrl, setAbortCtrl] = useState<AbortController | null>(null);
  const [error, setError] = useState<string | null>(null);
  // v3.3 §4 — coarse-grained generation phase derived from the SSE
  // stream. "connecting" before any chunk arrives, "streaming" once
  // the first delta lands, "rendering" after the report-id sentinel.
  const [genPhase, setGenPhase] = useState<
    "idle" | "connecting" | "streaming" | "rendering"
  >("idle");

  // LLM & Reports v3.2 — preview modal + admin-override toggle for the
  // token-budget gate. The override is only consulted when the user
  // holds Reports.GenerateLargeBudget; the flag itself flips off when
  // attachments change (we want a fresh accept-the-budget click after
  // every modification).
  const [previewOpen, setPreviewOpen] = useState(false);
  const [budgetOverride, setBudgetOverride] = useState(false);
  const { hasPermission } = useAuth();
  const canOverrideBudget = hasPermission("Reports.GenerateLargeBudget");

  // Default DMS folder for v3.2 attachment uploads. The user doesn't
  // pick a folder explicitly (the spec hides that detail); we send the
  // first DMS root the API returns as the parent. Backend's
  // SubmissionDocumentsService validates the folder is real before
  // accepting the attach call.
  const folderRoots = useQuery({
    queryKey: ["folders", "roots"],
    queryFn: () => foldersApi.roots(),
  });
  const defaultUploadFolderId = folderRoots.data?.[0]?.id ?? null;

  const template = useMemo<ReportTemplateDto | undefined>(
    () => templates.data?.find((t) => t.id === reportTemplateId),
    [templates.data, reportTemplateId],
  );

  // When a template is picked, fetch the full schema (responses-friendly).
  const templateDetail = useQuery({
    queryKey: ["report-template", reportTemplateId],
    queryFn: () => reportTemplatesApi.get(reportTemplateId),
    enabled: !!reportTemplateId,
  });

  // Resume an existing draft submission via ?submissionId=... — pre-fills
  // form responses + the template selection.
  const draft = useQuery({
    queryKey: ["report-submission", submissionIdParam],
    queryFn: () => reportSubmissionsApi.get(submissionIdParam!),
    enabled: !!submissionIdParam,
  });

  useEffect(() => {
    if (!draft.data) return;
    setReportTemplateId(draft.data.formTemplateId);
    setSubmissionId(draft.data.id);
    if (draft.data.status !== "Draft") setSubmitted(true);
    const initial: Record<string, string> = {};
    for (const r of draft.data.responses) {
      if (r.responseText) initial[r.formQuestionId] = r.responseText;
    }
    // v3.4/v3.5: hydrate the standard "Report subject" + "Audience"
    // virtual fields from the dedicated submission columns so resuming
    // a saved draft restores both values into the responses map (keyed
    // by their sentinel question ids).
    if (draft.data.preparedFor) {
      initial[PREPARED_FOR_QUESTION_ID] = draft.data.preparedFor;
    }
    if (draft.data.audience) {
      initial[AUDIENCE_QUESTION_ID] = draft.data.audience;
    }
    setResponses((cur) => ({ ...initial, ...cur }));
  }, [draft.data]);

  // Deal work-tab deep link — fetch the pre-drafted intake and apply it ONCE
  // after the deep-linked template's questions are loaded (the goal answer is
  // keyed by a question id that only the template detail can confirm).
  // Draft resume takes precedence; absent/invalid params leave the wizard on
  // its normal blank behavior. Every prefilled value lands in ordinary state,
  // so the user edits freely and validation runs unchanged.
  const itemPrefill = useReportItemPrefill(
    itemCodeParam,
    dealIdForPayload,
    !submissionIdParam,
  );
  const prefillAppliedRef = useRef(false);
  useEffect(() => {
    if (prefillAppliedRef.current || submissionIdParam) return;
    const prefill = itemPrefill.data;
    if (!prefill) return;
    if (!templateDetail.data || templateDetail.data.id !== prefill.templateId)
      return;
    prefillAppliedRef.current = true;
    setTitle((cur) => (cur.trim().length > 0 ? cur : prefill.title));
    setResponses((cur) => {
      const next = { ...cur };
      if (!next[PREPARED_FOR_QUESTION_ID]) {
        next[PREPARED_FOR_QUESTION_ID] = prefill.subject;
      }
      if (!next[AUDIENCE_QUESTION_ID]) {
        next[AUDIENCE_QUESTION_ID] = prefill.audience;
      }
      if (
        prefill.goalQuestionId &&
        prefill.goalText &&
        !next[prefill.goalQuestionId]
      ) {
        next[prefill.goalQuestionId] = prefill.goalText;
      }
      return next;
    });
  }, [itemPrefill.data, templateDetail.data, submissionIdParam]);

  const masterDoc = useQuery({
    queryKey: ["central-document", template?.masterDocumentId],
    queryFn: () => documentsApi.get(template!.masterDocumentId!),
    enabled: !!template?.masterDocumentId,
  });

  // Required-field validation (client-side only — server re-validates on submit).
  const missingRequired = useMemo(() => {
    if (!templateDetail.data) return [] as ReportFormQuestionDto[];
    const all = templateDetail.data.sections.flatMap((s) => s.questions);
    // v3.4 §1: virtual system questions never block submit — they're
    // optional by spec and live outside the regular validation path.
    return all.filter((q) => q.isRequired && !isSystemQuestion(q.id) && !responses[q.id]);
  }, [templateDetail.data, responses]);

  /**
   * v3.4/v3.5 — split the live `responses` map into the wire-shape
   * payload. The two virtual system fields (Report subject + Audience)
   * ride on `preparedFor` and `audience`, not the responses array,
   * because they don't have backing ReportTemplateQuestion rows.
   * Returns the remaining answers as the responses payload.
   */
  const buildSubmissionPayload = (): CreateReportSubmissionPayload => {
    const realResponses: CreateReportSubmissionPayload["responses"] = [];
    let preparedFor: string | null = null;
    let audience: string | null = null;
    for (const [formQuestionId, responseText] of Object.entries(responses)) {
      if (isSystemQuestion(formQuestionId)) {
        // Empty / whitespace counts as "not set"; server normalizes
        // to null but we send null to keep the wire payload tidy.
        const trimmed = (responseText ?? "").trim();
        const value = trimmed.length > 0 ? trimmed : null;
        if (formQuestionId.toLowerCase() === AUDIENCE_QUESTION_ID) {
          audience = value;
        } else {
          preparedFor = value;
        }
        continue;
      }
      realResponses.push({ formQuestionId, responseText });
    }
    return {
      formTemplateId: reportTemplateId,
      // Deal work-tab deep link — associates the submission (and the
      // generated report) back to the originating deal. Null when the page
      // was opened normally; the server keeps an existing association on
      // draft updates when null.
      dealId: dealIdForPayload,
      // Broker certification deep link — same association semantics as
      // dealId (null when the page was opened normally).
      brokerId: brokerIdForPayload,
      responses: realResponses,
      preparedFor,
      audience,
    };
  };

  const draftMutation = useMutation({
    mutationFn: async () => {
      if (!templateDetail.data) throw new Error("Pick a template first.");
      const payload = buildSubmissionPayload();
      if (submissionId) {
        return reportSubmissionsApi.updateDraft(submissionId, payload);
      }
      return reportSubmissionsApi.create(payload);
    },
    onSuccess: (s: ReportSubmissionDto) => {
      setSubmissionId(s.id);
      setSearchParams((p) => {
        p.set("submissionId", s.id);
        return p;
      });
      toast({ tone: "success", message: "Draft saved." });
    },
    onError: (e: Error) => toast({ tone: "error", message: e.message }),
  });

  // LLM & Reports v3.2 §9.1 — lazy submission-creator handed to the
  // AnswerAssistantButtons. While the user is typing, no submission
  // row has been saved yet; the buttons need an id to log Improve /
  // Expand calls against. Calling this function saves the in-progress
  // form-fill as a draft (via the same draftMutation the explicit
  // Save Draft button uses) and resolves to the resulting id —
  // returning null means the parent has no template selected, which
  // the buttons surface as a toast and decline to open the modal.
  const ensureAssistantSubmission = async (): Promise<string | null> => {
    if (submissionId) return submissionId;
    if (!templateDetail.data) return null;
    const created = await draftMutation.mutateAsync();
    return created?.id ?? null;
  };

  const submitMutation = useMutation({
    mutationFn: async () => {
      let id = submissionId;
      const payload = buildSubmissionPayload();
      if (!id) {
        const created = await reportSubmissionsApi.create(payload);
        id = created.id;
        setSubmissionId(id);
      } else {
        await reportSubmissionsApi.updateDraft(id, payload);
      }
      return reportSubmissionsApi.submit(id);
    },
    onSuccess: () => {
      setSubmitted(true);
      toast({ tone: "success", message: "Form submitted. Review the prompt and generate." });
    },
    onError: (e: Error) => {
      toast({ tone: "error", message: e.message });
      setError(e.message);
    },
  });

  // Resolve the prompt against the (now-submitted) submission. Reuses the
  // admin /test endpoint — when called against a real submission, it
  // returns the same prompt the streaming generator will assemble.
  const previewMutation = useMutation({
    mutationFn: () =>
      reportTemplatesApi.adminTest(reportTemplateId, {
        reportSubmissionId: submissionId,
        supportingDocumentIds: [],
        oneTimePromptEdit: null,
      }),
    onSuccess: (r) => {
      setResolvedPrompt(r.resolvedPrompt);
      setEditedPrompt(r.resolvedPrompt);
    },
  });

  // Fetch the resolved prompt automatically when we land on the prompt
  // review step. Re-runs when the supporting doc selection changes.
  useEffect(() => {
    if (!submitted || !submissionId) return;
    previewMutation.mutate();
    // Intentionally narrow deps — we re-resolve on the dependencies the
    // assembled prompt actually depends on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitted, submissionId]);

  // All report templates now use the v3.6 attachment flow for supporting
  // docs. The picker attaches by reference (creating ReportSubmissionDocument
  // rows with a reports-conversion record), which routes generation onto the
  // self-healing content-block path and surfaces per-document conversion
  // status up front. This retires the legacy v3.1 SupportingDocsPicker, whose
  // KB-ingestion "Ingested" badge didn't match the conversion record that
  // generation requires (the "has no ingestion record" / HTTP 400 failures).

  // Token budget gate (drives the documents → review step transition).
  const previewQuery = usePreviewPrompt(submitted ? submissionId : null);
  const tokenBudget = previewQuery.data?.tokenBudget ?? null;
  const exceedsTokenBudget = tokenBudget?.exceedsThreshold ?? false;
  const budgetGateOpen =
    !exceedsTokenBudget || (canOverrideBudget && budgetOverride);

  // v3.6 — readiness gate. No attached document may be left Pending/Failed at
  // Generate time; every row must be Ready (Succeeded) or Skipped. The user
  // sees per-document status + Retry (in SubmissionDocumentsSection) instead
  // of a dead-end error on Generate.
  const submissionDocs = useSubmissionDocuments(submitted ? submissionId : null);
  const attachmentsReady = allAttachmentsReady(submissionDocs.data ?? []);
  const blockingAttachments = (submissionDocs.data ?? []).filter(
    (r) => r.conversionStatus === "Pending" || r.conversionStatus === "Failed",
  );

  // Reset the override toggle whenever the budget number actually
  // changes — protects against a user uploading more attachments
  // after flipping the toggle on a smaller payload.
  useEffect(() => {
    setBudgetOverride(false);
  }, [tokenBudget?.totalTokens]);

  // v3.3 §3 — auto-clear the "Edits saved" confirmation pill after
  // ~3s. Cleared early if the user starts editing again (handled in
  // the textarea onChange below).
  useEffect(() => {
    if (promptSavedAt === null) return;
    const timer = window.setTimeout(() => setPromptSavedAt(null), 3000);
    return () => window.clearTimeout(timer);
  }, [promptSavedAt]);

  // Five-step progress strip visible above the page. Steps 6-8
  // (Drafted → Branding → Finalized) live on Stage 1's
  // ReportV32DetailPage; the wizard ends at "generate" and the user
  // routes to /reports/:id afterwards.
  const wizardStep: WizardStepKey = !reportTemplateId
    ? "title"
    : !title.trim()
      ? "title"
      : !submitted
        ? "form"
        : !budgetGateOpen
          ? "documents"
          : !resolvedPrompt
            ? "review"
            : "generate";

  const completedSteps: WizardStepKey[] = [
    ...(reportTemplateId && title.trim() ? (["title"] as const) : []),
    ...(submitted ? (["form"] as const) : []),
    ...(submitted && budgetGateOpen ? (["documents"] as const) : []),
    ...(submitted && resolvedPrompt ? (["review"] as const) : []),
  ];

  const canGenerate =
    !!reportTemplateId &&
    !!submissionId &&
    submitted &&
    !!title.trim() &&
    !streaming &&
    budgetGateOpen &&
    attachmentsReady;

  const startGenerate = async () => {
    if (!canGenerate) return;
    setError(null);
    setStreamedText("");
    setStreamedReportId(null);
    setStreaming(true);
    setGenPhase("connecting");
    const ctrl = await reportGenerationApi.stream(
      {
        reportTemplateId,
        title: title.trim(),
        reportSubmissionId: submissionId!,
        // Supporting docs ride as ReportSubmissionDocument attachments now,
        // so the legacy supportingDocumentIds field is always empty — the
        // backend assembles them as content blocks from the attached rows.
        supportingDocumentIds: [],
        // v3.3 §3: send the committed Save edits value, not the live
        // textarea — the user explicitly opted in by clicking Save.
        oneTimePromptEdit:
          committedPromptEdit !== null && committedPromptEdit !== resolvedPrompt
            ? committedPromptEdit
            : null,
      },
      (chunk) => {
        setStreamedText((cur) => cur + chunk);
        // First chunk flips the phase from connecting → streaming.
        setGenPhase((p) => (p === "connecting" ? "streaming" : p));
      },
      (reportId) => {
        setStreaming(false);
        setAbortCtrl(null);
        setGenPhase("idle");
        if (reportId) {
          setStreamedReportId(reportId);
          void qc.invalidateQueries({ queryKey: ["reports"] });
        }
      },
      (err) => {
        setStreaming(false);
        setAbortCtrl(null);
        setGenPhase("idle");
        setError(err.message);
      },
    );
    setAbortCtrl(ctrl);
  };

  const updateResponse = (questionId: string, value: string) =>
    setResponses({ ...responses, [questionId]: value });

  const onPickerPicked = (questionId: string, picked: PickerRecord) => {
    setResponses((r) => ({ ...r, [questionId]: picked.id }));
    setPickerLabels((r) => ({ ...r, [questionId]: picked.primary }));
    setActivePicker(null);
  };

  return (
    <>
      <PageHeader
        eyebrow="Reports"
        title="Generate report"
        description="Pick a template, fill out the form, attach any supporting documents, review the prompt, and generate your report."
      />

      <div className="space-y-space-4 max-w-reading mx-auto">
        {/* LLM & Reports v3.2 §8.1 — wizard progress strip. Five
            user-visible steps (title / form / documents / review /
            generate); the post-Drafted Markdown Review and Branded
            Output steps live on /reports/:id (Stage 1's detail page). */}
        <WizardProgress currentStep={wizardStep} completedSteps={completedSteps} />

        {/* Header — Template picker --------------------------------- */}
        <section className="bg-white-pure border border-border-light rounded-lg p-space-4">
          <FormField label="Report template" required htmlFor="rt-pick">
            <Select
              id="rt-pick"
              value={reportTemplateId}
              onChange={(e) => {
                setReportTemplateId(e.target.value);
                // Reset state on template switch — responses don't carry over.
                setSubmissionId(null);
                setSubmitted(false);
                setResponses({});
                setPickerLabels({});
                setResolvedPrompt("");
                setEditedPrompt("");
                setCommittedPromptEdit(null);
                setPromptSavedAt(null);
                setStreamedReportId(null);
                setStreamedText("");
                setSearchParams((p) => {
                  p.delete("submissionId");
                  return p;
                });
              }}
              disabled={submitted}
            >
              <option value="">Select a template…</option>
              {templates.data?.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </Select>
          </FormField>
          {template && (
            <div className="mt-space-2">
              <Badge status="info" className="mr-space-2">{template.category}</Badge>
              {template.masterDocumentId && <Badge status="gold">Master doc</Badge>}
              {template.description && (
                <p className="mt-space-2 text-small text-gray-mid">{template.description}</p>
              )}
            </div>
          )}
        </section>

        {/* Step 1 — Title ------------------------------------------- */}
        {reportTemplateId && (
          <section className="bg-white-pure border border-border-light rounded-lg p-space-4">
            <SectionHeader index={1} title="Title" required />
            <FormField label="Report title" required htmlFor="rt-title">
              <Input
                id="rt-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={template?.name ?? "Untitled report"}
                disabled={streaming}
              />
            </FormField>
          </section>
        )}

        {/* Step 2 — Form -------------------------------------------- */}
        {templateDetail.data && (
          <section className="bg-white-pure border border-border-light rounded-lg p-space-4">
            <div className="flex items-center justify-between">
              <SectionHeader index={2} title="Form" required />
              {submitted && (
                <span className="inline-flex items-center gap-space-1 text-caption text-status-success">
                  <Lock size={14} aria-hidden /> Locked
                </span>
              )}
            </div>
            {templateDetail.data.instructions && (
              <p className="text-small whitespace-pre-wrap text-gray-mid mb-space-3">
                {templateDetail.data.instructions}
              </p>
            )}
            <div className="space-y-space-3">
              {templateDetail.data.sections.map((section) => {
                const systemSection = isSystemSection(section.id);
                // v3.5 §5: the "Report context" virtual section gets a
                // different card treatment so the form-filler reads it
                // as metadata, not a numbered template question. Same
                // section-card chrome but with a label-style heading
                // and no number prefix.
                const cardClasses = systemSection
                  ? "rounded-md border border-dashed border-gold-shadow/50 bg-white-pure p-space-3"
                  : "border border-border-light rounded-md bg-white-warm p-space-3";
                return (
                <div
                  key={section.id}
                  className={cardClasses}
                  data-system-section={systemSection ? "true" : undefined}
                >
                  {systemSection ? (
                    <div className="mb-space-2">
                      <span className="font-display text-caption uppercase tracking-label text-gold-shadow">
                        {section.title}
                      </span>
                    </div>
                  ) : (
                    <h3 className="font-display font-bold text-h4 text-onyx tracking-normal mb-space-2">
                      {section.title}
                      {section.isRequired && (
                        <Badge status="gold" className="ml-space-2">Required</Badge>
                      )}
                    </h3>
                  )}
                  {section.description && (
                    <p className="text-caption text-gray-mid mb-space-2">{section.description}</p>
                  )}
                  <div className="space-y-space-2">
                    {section.questions.map((q) => (
                      <FormField
                        key={q.id}
                        label={q.questionText}
                        required={q.isRequired}
                        helperText={q.helpText ?? undefined}
                      >
                        {renderQuestionInput({
                          q,
                          value: responses[q.id] ?? "",
                          pickerLabel: pickerLabels[q.id] ?? null,
                          disabled: submitted,
                          // LLM & Reports v3.2 §9.1 — Improve /
                          // Expand visibility is driven purely by the
                          // textarea's value (the AnswerAssistantButtons
                          // component owns the show-when-text-present
                          // gate); the parent supplies the existing
                          // submissionId when one is already in flight
                          // and a lazy `ensureAssistantSubmission` for
                          // the case where the user is still typing
                          // and no draft has been saved yet.
                          assistantSubmissionId: submissionId,
                          ensureAssistantSubmission,
                          onChange: (v) => updateResponse(q.id, v),
                          onOpenPicker: (kind) =>
                            setActivePicker({ questionId: q.id, kind }),
                        })}
                      </FormField>
                    ))}
                  </div>
                </div>
                );
              })}
            </div>
            {!submitted && (
              <div className="flex items-center justify-end gap-space-2 mt-space-3">
                {missingRequired.length > 0 && (
                  <p className="text-caption text-status-warning mr-auto">
                    Missing {missingRequired.length} required field
                    {missingRequired.length === 1 ? "" : "s"}.
                  </p>
                )}
                <Button
                  variant="secondary"
                  onClick={() => draftMutation.mutate()}
                  loading={draftMutation.isPending}
                  disabled={!reportTemplateId}
                >
                  <Save size={14} aria-hidden /> Save draft
                </Button>
                <Button
                  onClick={() => submitMutation.mutate()}
                  loading={submitMutation.isPending}
                  disabled={missingRequired.length > 0}
                >
                  Submit form <ArrowRight size={14} aria-hidden />
                </Button>
              </div>
            )}
          </section>
        )}

        {/* Step 3 — Master Document --------------------------------- */}
        {submitted && template && (
          <section className="bg-white-pure border border-border-light rounded-lg p-space-4">
            <SectionHeader index={3} title="Master document" />
            {template.masterDocumentId ? (
              masterDoc.isLoading ? (
                <p className="text-caption text-gray-mid">Loading master document…</p>
              ) : masterDoc.data ? (
                <div className="border border-border-light rounded-md bg-white-warm p-space-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-semibold text-onyx">{masterDoc.data.title}</div>
                      <div className="text-caption text-gray-mid">
                        {masterDoc.data.fileName}
                      </div>
                    </div>
                    <Badge status="gold">Pre-loaded</Badge>
                  </div>
                  <p className="text-caption text-gray-mid mt-space-2">
                    The master document is bound to this template by an Admin and is
                    automatically included as the {"{{master_document}}"} block in
                    the assembled prompt.
                  </p>
                </div>
              ) : (
                <p className="text-caption text-status-warning">
                  Master document is missing or inaccessible.
                </p>
              )
            ) : (
              <p className="text-caption text-gray-mid italic">
                No master document for this report. The unified pipeline will
                generate from your form responses and any supporting documents you
                add below.
              </p>
            )}
          </section>
        )}

        {/* Step 4 — Supporting Documents ---------------------------- */}
        {submitted && (
          <section className="bg-white-pure border border-border-light rounded-lg p-space-4">
            <SectionHeader index={4} title="Supporting documents" />
            <p className="text-caption text-gray-mid mb-space-3">
              Browse the repository or drop Word, Excel, PDF, or image files
              here. Each gets a tag so Claude can reason about the source — pick
              the closest match from the dropdown after attaching.
            </p>
            {submissionId && defaultUploadFolderId ? (
              <SubmissionDocumentsSection
                submissionId={submissionId}
                folderId={defaultUploadFolderId}
                locked={streaming}
              />
            ) : (
              <p className="text-caption text-gray-mid">Loading uploader…</p>
            )}
          </section>
        )}

        {/* Step 5 — Prompt Review ----------------------------------- */}
        {submitted && (
          <section className="bg-white-pure border border-border-light rounded-lg p-space-4">
            <div className="flex items-center justify-between">
              <SectionHeader index={5} title="Prompt review" />
              <div className="flex items-center gap-space-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPreviewOpen(true)}
                  disabled={!submissionId}
                >
                  <FileText size={14} aria-hidden /> Preview to Claude
                </Button>
                {editingPrompt ? (
                  <>
                    {/* v3.3 §3: Save edits commits the live textarea
                        value to committedPromptEdit, which is what
                        startGenerate sends. Disabled until the user
                        actually changes something since last save. */}
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => {
                        setCommittedPromptEdit(editedPrompt);
                        setPromptSavedAt(Date.now());
                      }}
                      disabled={editedPrompt === (committedPromptEdit ?? resolvedPrompt)}
                    >
                      <Save size={14} aria-hidden /> Save edits
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        setEditedPrompt(resolvedPrompt);
                        setCommittedPromptEdit(null);
                        setPromptSavedAt(null);
                        setEditingPrompt(false);
                      }}
                    >
                      <RotateCcw size={14} aria-hidden /> Reset
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setEditingPrompt(true)}
                    disabled={!resolvedPrompt}
                  >
                    <Pencil size={14} aria-hidden /> Edit
                  </Button>
                )}
              </div>
            </div>
            {previewMutation.isPending && !resolvedPrompt && (
              <p className="text-caption text-gray-mid">Resolving prompt…</p>
            )}
            {previewMutation.isError && (
              <p className="text-caption text-status-error" role="alert">
                Could not resolve the prompt: {(previewMutation.error as Error)?.message}
              </p>
            )}
            {resolvedPrompt && (
              <Textarea
                value={editingPrompt ? editedPrompt : resolvedPrompt}
                readOnly={!editingPrompt}
                onChange={(e) => {
                  setEditedPrompt(e.target.value);
                  // Clear the saved-confirmation pill the moment the
                  // user types again so the message can't lie about
                  // what's pending.
                  if (promptSavedAt !== null) setPromptSavedAt(null);
                }}
                rows={16}
                className="font-mono text-caption"
              />
            )}
            {editingPrompt && (
              <div className="mt-space-1 flex items-center gap-space-2">
                <p className="text-caption text-gray-mid">
                  One-time edit — does not change your saved prompt customization.
                </p>
                {promptSavedAt !== null && (
                  <span
                    className="inline-flex items-center gap-space-1 rounded-full bg-status-success/10 text-status-success px-space-2 py-[2px] text-caption font-semibold"
                    role="status"
                  >
                    <Check size={12} aria-hidden /> Edits saved — will be used on next Generate
                  </span>
                )}
              </div>
            )}
          </section>
        )}

        {/* Step 6 — Generate ---------------------------------------- */}
        {submitted && (
          <section className="bg-white-pure border border-border-light rounded-lg p-space-4">
            <div className="flex items-center justify-between gap-space-3">
              <div>
                <SectionHeader index={6} title="Generate" />
                <p className="text-caption text-gray-mid">
                  Streams a Drafted markdown — review it before applying
                  branding.
                </p>
              </div>
              {streaming ? (
                <div className="flex items-center gap-space-3">
                  {/* v3.3 §4: spinner + status line. The spinner uses
                      the gold accent (matches Phoenix tokens) and
                      Loader2 from lucide rotates via animate-spin. */}
                  <span
                    className="inline-flex items-center gap-space-2 text-caption text-gray-mid"
                    role="status"
                    aria-live="polite"
                  >
                    <Loader2 size={16} className="animate-spin text-gold-shadow" aria-hidden />
                    <span>{describeGenPhase(genPhase)}</span>
                  </span>
                  <Button variant="destructive" onClick={() => abortCtrl?.abort()}>
                    <Square size={16} aria-hidden /> Stop
                  </Button>
                </div>
              ) : (
                <Button
                  onClick={() => void startGenerate()}
                  disabled={!canGenerate}
                >
                  <PlayCircle size={16} aria-hidden /> Generate
                </Button>
              )}
            </div>

            <div className="mt-space-3 space-y-space-2">
              <TokenBudgetDisplay
                estimate={tokenBudget}
                canOverride={canOverrideBudget}
              />
              {canOverrideBudget && exceedsTokenBudget && (
                <label className="inline-flex items-center gap-space-2 font-body text-caption text-onyx">
                  <input
                    type="checkbox"
                    checked={budgetOverride}
                    onChange={(e) => setBudgetOverride(e.target.checked)}
                    className="h-4 w-4"
                  />
                  Override budget limit (admin only — usage will still be logged)
                </label>
              )}
            </div>
            {!attachmentsReady && blockingAttachments.length > 0 && (
              <p className="text-caption text-status-warning mt-space-3" role="status">
                {blockingAttachments.length} supporting document
                {blockingAttachments.length === 1 ? " is" : "s are"} still
                converting or failed. Wait for conversion, retry, or remove them
                in Step 4 before generating.
              </p>
            )}
            {error && (
              <p className="text-caption text-status-error mt-space-3" role="alert">
                {error}
              </p>
            )}
            {(streamedText || streaming) && (
              <div className="mt-space-3 border border-border-light rounded-md bg-white-warm p-space-3 max-h-[24rem] overflow-y-auto">
                <pre className="font-mono text-caption whitespace-pre-wrap text-onyx">
                  {streamedText}
                  {streaming && <span className="animate-pulse">▍</span>}
                </pre>
              </div>
            )}
            {streamedReportId && !streaming && (
              <div className="mt-space-3 flex items-center gap-space-2">
                <Button onClick={() => navigate(`/reports/${streamedReportId}`)}>
                  <FileDown size={16} aria-hidden /> Open report
                </Button>
                <span className="text-caption text-status-success">
                  <FileText size={12} className="inline" aria-hidden /> Saved.
                </span>
              </div>
            )}
          </section>
        )}
      </div>

      {/* Picker drawer (mounted at root so it slides over everything) */}
      {activePicker && (
        <PickerDrawer
          open
          kind={activePicker.kind}
          selectedId={responses[activePicker.questionId] ?? null}
          onClose={() => setActivePicker(null)}
          onPick={(picked) => onPickerPicked(activePicker.questionId, picked)}
        />
      )}

      {/* LLM & Reports v3.2 §6.7 — "Preview what's being sent to Claude"
          modal. Only mounted when the user clicks the button on Step 5;
          the underlying preview-prompt query stays disabled until then. */}
      <PreviewPromptModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        submissionId={previewOpen ? submissionId : null}
      />
    </>
  );
}

/**
 * v3.3 §4 — friendly label for each phase of the Generate run. Wired
 * to the SSE stream: connecting before any chunk arrives, streaming
 * once deltas land, rendering once the report-id sentinel is seen.
 */
function describeGenPhase(phase: "idle" | "connecting" | "streaming" | "rendering"): string {
  switch (phase) {
    case "connecting":
      return "Connecting to Claude…";
    case "streaming":
      return "Streaming response…";
    case "rendering":
      return "Rendering DOCX…";
    case "idle":
    default:
      return "Working…";
  }
}

function SectionHeader({
  index,
  title,
  required,
}: {
  index: number;
  title: string;
  required?: boolean;
}) {
  return (
    <h2 className="font-display font-bold text-h3 text-onyx mb-space-2 tracking-normal">
      <span className="text-gold-shadow mr-space-2 font-mono">{index}.</span>
      {title}
      {required && <Badge status="gold" className="ml-space-2">Required</Badge>}
    </h2>
  );
}

function renderQuestionInput({
  q,
  value,
  pickerLabel,
  disabled,
  assistantSubmissionId,
  ensureAssistantSubmission,
  onChange,
  onOpenPicker,
}: {
  q: ReportFormQuestionDto;
  value: string;
  pickerLabel: string | null;
  disabled: boolean;
  assistantSubmissionId: string | null;
  ensureAssistantSubmission: () => Promise<string | null>;
  onChange: (v: string) => void;
  onOpenPicker: (kind: PickerKind) => void;
}) {
  if (isPickerType(q.questionType)) {
    return (
      <div className="flex items-center gap-space-2">
        <Input
          readOnly
          value={pickerLabel ?? value}
          placeholder="No record selected"
          disabled={disabled}
          className="flex-1"
        />
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => onOpenPicker(q.questionType as PickerKind)}
          disabled={disabled}
        >
          {value ? "Change" : "Pick"}
        </Button>
      </div>
    );
  }
  switch (q.questionType) {
    case "TextArea":
      return (
        <>
          <LongFormTextarea
            value={value}
            onChange={onChange}
            disabled={disabled}
          />
          <AnswerAssistantButtons
            reportSubmissionId={assistantSubmissionId}
            questionId={q.id}
            value={value}
            onChange={onChange}
            ensureSubmission={ensureAssistantSubmission}
          />
        </>
      );
    case "Number":
      return (
        <Input
          type="number"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        />
      );
    case "Date":
      return (
        <Input
          type="date"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        />
      );
    case "Select":
    case "MultiSelect": {
      const opts = safeParseArray(q.optionsJson);
      return (
        <Select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        >
          <option value="">Select…</option>
          {opts.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </Select>
      );
    }
    case "YesNo":
      return (
        <Select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        >
          <option value="">Select…</option>
          <option value="Yes">Yes</option>
          <option value="No">No</option>
        </Select>
      );
    case "RatingScale":
      return (
        <Select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        >
          <option value="">Rate 1-5…</option>
          {[1, 2, 3, 4, 5].map((n) => (
            <option key={n} value={String(n)}>
              {n}
            </option>
          ))}
        </Select>
      );
    case "FileAttachment":
      return <Input type="file" disabled />;
    case "Text":
    default:
      return (
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        />
      );
  }
}

function safeParseArray(s?: string | null): string[] {
  if (!s) return [];
  try {
    const parsed = JSON.parse(s);
    if (Array.isArray(parsed)) return parsed.map(String);
  } catch {
    // fall through
  }
  return [];
}
